import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import Questions from './Questions/Questions';
import useForm from '../useForm';
import { FormEditContext } from './FormEditContext';
import { patchForm } from '../../../../services/forms/formsService';
import { UserContext } from '../../../../utils/contexts/UserContext';
import { isSSA } from '../../../../utils/constants/roles';
import { Button } from '../../../../components/forms/Buttons/Button';
import {
  FORM_STATUS,
  FORM_STATUS_LABELS,
  toastFormError,
} from '../../formUtils';
//   import Responses from './Responses/Responses';
//   import useResponses from './Responses/useResponses';

const readOnlyReason = (form) => {
  if (form.year_has_ended) {
    return `Форма ${form.exam_year_year} года закрыта навсегда: формы прошлых лет менять нельзя.`;
  }
  if (form.status === FORM_STATUS.CLOSED) {
    return 'Редактирование формы закрыто. Открыть его может только супер-супер админ (SSA).';
  }
  return 'Нет прав на редактирование.';
};

const EditForm = () => {
  const { uuid } = useParams();
  const { user } = useContext(UserContext);
  const formState = useForm(uuid);
  const { form, fetchForm } = formState;
  const [tab, setTab] = useState('questions');
  const [statusSaving, setStatusSaving] = useState(false);
  // const { count, isLoading, setPage, users } = useResponses(uuid);
  const tabRefs = useRef({});
  //   const { state } = useLocation();

  // Права не выводим на фронте: can_edit от бэка уже учитывает роль, статус
  // формы и закончившийся год. Пока форма не загружена — только чтение.
  const readOnly = !form?.can_edit;
  const canChangeStatus =
    isSSA(user) &&
    !!form &&
    !form.year_has_ended &&
    Object.values(FORM_STATUS).includes(form.status);

  const reportError = useCallback(
    (err, fallback) => {
      toastFormError(err, fallback);
      // 403 — форму закрыли или заморозили, пока она была открыта:
      // перечитываем, чтобы редактор сам ушёл в чтение и показал причину.
      if (err?.response?.status === 403) fetchForm();
    },
    [fetchForm],
  );

  const editContext = useMemo(
    () => ({ readOnly, reportError }),
    [readOnly, reportError],
  );

  const toggleStatus = () => {
    const status =
      form.status === FORM_STATUS.EDIT ? FORM_STATUS.CLOSED : FORM_STATUS.EDIT;

    setStatusSaving(true);
    patchForm(uuid, { status })
      // can_edit зависит от статуса — перечитываем форму целиком
      .then(() => fetchForm())
      .catch((err) => toastFormError(err, 'Не удалось изменить статус формы'))
      .finally(() => setStatusSaving(false));
  };

  const tabs = useMemo(
    () => [
      {
        key: 'questions',
        text: 'Questions',
      },
      // {
      //   key: 'responses',
      //   text: (
      //     <span className='flex flex-row gap-3'>
      //       Responses{' '}
      //       {!!count && (
      //         <div
      //           className={`flex flex-col justify-center text-white rounded-full  text-xs px-1 ${
      //             tab === 'responses' ? 'bg-violet-700' : 'bg-slate-700'
      //           }`}
      //         >
      //           {count}
      //         </div>
      //       )}
      //     </span>
      //   ),
      // },
      // { key: 'settings', text: 'Settings' },
    ],
    [],
    //   [count, tab],
  );

  // useEffect(() => {
  //   if (state?.tab) {
  //     setTab(tabs[state?.tab]?.key);
  //     state.tab = null;
  //   }
  //   if (state?.user) {
  //     console.log(
  //       users?.indexOf(users?.find((u) => u.user.uuid === state.user)),
  //     );
  //     setPage(users?.indexOf(users?.find((u) => u.user.uuid === state.user)));
  //     state.user = null;
  //   }
  // }, [state, tabs, users, setPage]);

  // const updateTabBorder = useCallback(() => {
  //   const activeTab = tabRefs.current[tab];
  //   const tabBorder = document.querySelector('.tab-border');

  //   if (activeTab && tabBorder) {
  //     const tabWidth = activeTab.offsetWidth;
  //     const tabLeft = activeTab.offsetLeft;

  //     tabBorder.style.width = `${tabWidth}px`;
  //     tabBorder.style.left = `${tabLeft}px`;
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [tab, count]);

  // useEffect(() => {
  //   updateTabBorder();
  //   window.addEventListener('resize', updateTabBorder);
  //   return () => {
  //     window.removeEventListener('resize', updateTabBorder);
  //   };
  // }, [updateTabBorder]);

  return (
    <div className=' relative flex max-h-[93vh] min-h-[93vh] flex-col items-center bg-slate-200'>
      <div className='relative flex h-16 min-h-[4rem] w-full flex-row items-end justify-center gap-2 border-y bg-white pb-1 font-semibold text-slate-500'>
        {tabs.map((t, i) => {
          return (
            <div
              key={t.key}
              ref={(el) => (tabRefs.current[t.key] = el)}
              className={`${tab === t.key && 'text-violet-700'} px-2 py-1`}
              onClick={() => {
                setTab(t.key);
              }}
            >
              {t.text}
            </div>
          );
        })}
        <div
          className={`tab-border absolute bottom-0  h-0.5 bg-violet-700 transition-all duration-300`}
        />
        {form && (
          <div className='absolute left-4 top-1/2 flex -translate-y-1/2 flex-row items-center gap-2 text-sm font-normal'>
            <span>
              {form.exam_year_year ? `${form.exam_year_year} год` : 'Без года'}
            </span>
            {/* у формы прошлого года статус ни на что не влияет */}
            {!form.year_has_ended && FORM_STATUS_LABELS[form.status] && (
              <span
                className={`rounded px-2 py-0.5 text-xs font-semibold ${
                  form.status === FORM_STATUS.EDIT
                    ? 'bg-green-100 text-green-700'
                    : 'bg-slate-100 text-slate-600'
                }`}
              >
                {FORM_STATUS_LABELS[form.status]}
              </span>
            )}
          </div>
        )}
        {canChangeStatus && (
          <div className='absolute right-4 top-1/2 -translate-y-1/2 font-normal'>
            <Button
              text={
                form.status === FORM_STATUS.EDIT
                  ? 'Закрыть редактирование'
                  : 'Открыть для редактирования'
              }
              color={
                form.status === FORM_STATUS.EDIT
                  ? 'secondary'
                  : 'success-simple'
              }
              className='text-sm'
              loading={statusSaving}
              disabled={statusSaving}
              onClick={toggleStatus}
            />
          </div>
        )}
      </div>
      {form && readOnly && (
        <div className='flex w-full flex-row items-center justify-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800'>
          <LockClosedIcon className='h-4 min-w-fit' />
          {readOnlyReason(form)}
        </div>
      )}
      <div className='flex h-fit w-full flex-row justify-center overflow-y-scroll p-12'>
        <div className='h-fit'>
          <FormEditContext.Provider value={editContext}>
            {/* disabled гасит поля и кнопки, pointer-events-none — клики по
                иконкам (корзина, «+», крестики). contentEditable и drag-n-drop
                выключают сами компоненты по readOnly из контекста. */}
            <fieldset
              disabled={readOnly}
              className={readOnly ? 'pointer-events-none' : ''}
            >
              {tab === 'questions' && <Questions {...formState} />}
              {/* {tab === 'responses' && <Responses />} */}
            </fieldset>
          </FormEditContext.Provider>
        </div>
      </div>
    </div>
  );
};

export default EditForm;
